import { useCallback, useEffect, useMemo, useState } from 'react';

import { IncomingInventorizationOrder, configApi } from '../services/configApi';

/**
 * The incoming orders of the terminal an operator can attribute a found pallet to, held in
 * memory: the lookup filters the list without a round trip per keystroke. Loaded in every
 * mode, because the order can be corrected on any record the panel opens.
 */
export const useInventorizationIncomingOrders = (terminalId: number | undefined) => {
  const [orders, setOrders] = useState<IncomingInventorizationOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    if (!terminalId) {
      // An aborted load skips its own state updates, so what it left behind is cleared here.
      setOrders([]);
      setIsLoading(false);
      setHasLoadFailed(false);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setHasLoadFailed(false);

    configApi.getIncomingInventorizationOrders(terminalId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setOrders(data);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('Failed to fetch incoming inventorization orders', err);
        setOrders([]);
        setHasLoadFailed(true);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [terminalId, reloadCount]);

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  const orderById = useMemo(
    () => new Map(orders.map((order) => [order.order_id, order])),
    [orders]
  );

  const findOrderById = useCallback(
    (orderId: number) => orderById.get(orderId),
    [orderById]
  );

  return { orders, findOrderById, isLoading, hasLoadFailed, reload };
};
